import { Box, Text, VStack, HStack, Circle, Spinner } from '@chakra-ui/react';

import { MessageAttachment } from '@/features/messages/messages.types';
import { RefObject } from 'react';
import { FiPlay, FiPause } from 'react-icons/fi';
import { getAttachmentBgColor } from './conversation-utils';
import { AudioTranscription } from './audio-transcription';

export const MessageAttachmentAudio = ({
  isUser,
  isAIAgent,
  attachment,
  attachmentId,
  toggleAudio,
  isPlaying,
  progress,
  audioRefs,
  formatDuration,
  messageStatus,
  transcription,
}: {
  isUser: boolean;
  isAIAgent: boolean;
  attachment: MessageAttachment;
  attachmentId: string;
  toggleAudio: (audioId: string, audioUrl: string) => void;
  isPlaying: { [key: string]: boolean };
  messageStatus: string;
  progress: { [key: string]: number };
  audioRefs: RefObject<{ [key: string]: HTMLAudioElement | null }>;
  formatDuration: (seconds: number) => string;
  transcription: string | null;
}) => {
  if (!attachment) {
    return null;
  }

  console.log('attachment.duration_audio', attachment.duration_audio);

  return (
    <VStack
      gap={2}
      align="stretch"
      alignItems={isUser ? 'flex-start' : 'flex-end'}
      opacity={
        messageStatus === 'pending' || messageStatus === 'sending' ? 0.8 : 1
      }
    >
      {/* Add loading indicator for pending and sending states */}
      {(messageStatus === 'pending' || messageStatus === 'sending') && (
        <Spinner
          size="sm"
          position="absolute"
          top="3px"
          right="3px"
          color="white"
        />
      )}
      {/* Audio Player */}
      <Box
        width="100%"
        minWidth="250px" // Ensure a minimum width
        maxWidth="300px" // Prevent it from getting too wide
      >
        <Box
          bg={getAttachmentBgColor(isUser, isAIAgent)}
          borderRadius="lg"
          p={3}
          width="100%"
        >
          <HStack gap={3} width="100%" align="center">
            <Circle
              size="36px"
              bg="gray.500" // Darker gray for better affordance
              color="white"
              border="1px solid"
              borderColor="gray.500"
              boxShadow="sm"
              cursor="pointer"
              transition="all 0.15s ease-in-out"
              _hover={{ bg: 'gray.600', transform: 'scale(1.03)' }}
              _active={{ bg: 'gray.800', transform: 'scale(0.98)' }}
              _focusVisible={{ boxShadow: '0 0 0 2px rgba(255,255,255,0.9)' }}
              onClick={() => toggleAudio(attachmentId, attachment.file_url)}
              display="flex"
              justifyContent="center"
              alignItems="center"
              aria-label={
                isPlaying[attachmentId] ? 'Pause audio' : 'Play audio'
              }
              title={isPlaying[attachmentId] ? 'Pause' : 'Play'}
            >
              {isPlaying[attachmentId] ? (
                <FiPause size={16} />
              ) : (
                <FiPlay size={16} />
              )}
            </Circle>

            <Box flex="1">
              <Box
                bg="gray.600" // Darker track for stronger contrast
                borderRadius="full"
                height="6px"
                position="relative"
                overflow="hidden"
              >
                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  bottom={0}
                  width={`${progress[attachmentId] || 0}%`}
                  bg="gray.100" // Light indicator against dark track
                  transition="width 0.1s ease-in-out"
                />
              </Box>

              <HStack justify="space-between" mt={2}>
                <Text fontSize="xs" color="white">
                  {isPlaying[attachmentId] && audioRefs?.current?.[attachmentId]
                    ? formatDuration(
                        audioRefs?.current?.[attachmentId]!.currentTime,
                      )
                    : '0:00'}
                </Text>
                <Text fontSize="xs" color="white">
                  {attachment.duration_audio
                    ? attachment.duration_audio
                    : '0:00'}
                </Text>
              </HStack>
            </Box>
          </HStack>
        </Box>
      </Box>

      {/* Audio Transcription */}
      <AudioTranscription
        attachment={attachment}
        transcription={transcription}
      />
    </VStack>
  );
};
